import { Component, type ReactNode, type ErrorInfo } from 'react';

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Catches any unhandled render error inside the app and shows a
 * recoverable card instead of an unrecoverable blank page.
 *
 * Without this, a single uncaught throw inside any descendant unmounts
 * the entire React tree and the user sees a white screen with no clue
 * what went wrong. With this, the user sees the error message and a
 * "Reload" button, AND the error is logged to the console where we
 * can read it during a support session.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null, info: null });

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-2xl rounded-lg border border-destructive/30 bg-card p-6 shadow-lg">
          <h2 className="mb-2 text-lg font-semibold text-destructive">
            Une erreur est survenue
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            L&apos;application a rencontré un problème inattendu. Vos
            modifications offline restent enregistrées dans le journal de
            synchronisation.
          </p>
          <pre className="mb-4 max-h-48 overflow-auto rounded bg-muted p-3 text-xs">
            {this.state.error.message}
            {this.state.info?.componentStack && (
              <>
                {'\n\n'}
                {this.state.info.componentStack}
              </>
            )}
          </pre>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
            >
              Recharger l&apos;application
            </button>
          </div>
        </div>
      </div>
    );
  }
}
